import i18n from '@/utils/i18n';
import React from 'react';
import { AddressSearchInput } from '@/experiences/sales-portal/components/address-search-input/address-search-input';
import { AddressSearchResult } from '@/experiences/sales-portal/components/address-search-input/use-google-api-address-search-loader';
import { createStyles } from '@/contexts/theme-context';
import { InputCompound } from '@usepepper/sprout-kit-ui';
import { View } from 'react-native';
import type { EditAccountFormValues } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/blocs/mutators/use-edit-account-mutator';

export interface EditAccountFormErrors {
  accountId?: string;
  address?: string;
  address2?: string;
  name?: string;
  note?: string;
  routeStop?: string;
}

export interface EditAccountFormProps {
  addressSearchLoading?: boolean;
  addressSearchResults: AddressSearchResult[];
  errors: EditAccountFormErrors;
  hasNoAddressSearchResults?: boolean;
  onAddressSearch: (input: string) => void;
  onAddressSearchClose: (open: boolean) => void;
  onAddressSelect: (address: AddressSearchResult) => void;
  onEditAccount: <K extends keyof EditAccountFormValues>(
    field: K,
    value: EditAccountFormValues[K],
  ) => void;
  values: EditAccountFormValues;
}

export const EditAccountForm: React.FC<EditAccountFormProps> = ({
  hasNoAddressSearchResults,
  addressSearchLoading,
  addressSearchResults,
  errors,
  onAddressSearch,
  onAddressSearchClose,
  onAddressSelect,
  onEditAccount,
  values,
}) => {
  const [styles] = useStyles();

  return (
    <View style={styles.form}>
      <InputCompound error={!!errors.name} size="$44">
        <InputCompound.Label>
          {i18n.t('sales_portal.edit_account.labels.name')}
        </InputCompound.Label>
        <InputCompound.Box>
          <InputCompound.Section>
            <InputCompound.Area
              onChangeText={(text) => onEditAccount('name', text)}
              placeholder={i18n.t(
                'sales_portal.edit_account.placeholders.name',
              )}
              value={values.name}
            />
          </InputCompound.Section>
        </InputCompound.Box>
        {errors.name ? (
          <InputCompound.Info>{errors.name}</InputCompound.Info>
        ) : null}
      </InputCompound>

      <InputCompound error={!!errors.address} size="$44">
        <InputCompound.Label>
          {i18n.t('sales_portal.edit_account.labels.address')}
        </InputCompound.Label>
        <InputCompound.Box>
          <InputCompound.Section>
            <AddressSearchInput
              isEmpty={hasNoAddressSearchResults}
              isLoading={addressSearchLoading}
              onClose={onAddressSearchClose}
              onSearch={onAddressSearch}
              onSelect={onAddressSelect}
              results={addressSearchResults}
              value={values.address}
            />
          </InputCompound.Section>
        </InputCompound.Box>
        {errors.address ? (
          <InputCompound.Info>{errors.address}</InputCompound.Info>
        ) : null}
      </InputCompound>

      <InputCompound error={!!errors.address2} size="$44">
        <InputCompound.Label>
          {i18n.t('sales_portal.edit_account.labels.address2')}
          {i18n.t('sales_portal.edit_account.labels.optional')}
        </InputCompound.Label>
        <InputCompound.Box>
          <InputCompound.Section>
            <InputCompound.Area
              onChangeText={(text) => onEditAccount('address2', text)}
              placeholder={i18n.t(
                'sales_portal.edit_account.placeholders.address2',
              )}
              value={values.address2}
            />
          </InputCompound.Section>
        </InputCompound.Box>
        {errors.address2 ? (
          <InputCompound.Info>{errors.address2}</InputCompound.Info>
        ) : null}
      </InputCompound>

      <InputCompound error={!!errors.accountId} size="$44">
        <InputCompound.Label>
          {i18n.t('sales_portal.edit_account.labels.account_id')}
          {i18n.t('sales_portal.edit_account.labels.optional')}
        </InputCompound.Label>
        <InputCompound.Box>
          <InputCompound.Section>
            <InputCompound.Area
              onChangeText={(text) => onEditAccount('accountId', text)}
              placeholder={i18n.t(
                'sales_portal.edit_account.placeholders.account_id',
              )}
              value={values.accountId}
            />
          </InputCompound.Section>
        </InputCompound.Box>
        {errors.accountId ? (
          <InputCompound.Info>{errors.accountId}</InputCompound.Info>
        ) : null}
      </InputCompound>

      <InputCompound error={!!errors.routeStop} size="$44">
        <InputCompound.Label>
          {i18n.t('sales_portal.edit_account.labels.route_stop')}
          {i18n.t('sales_portal.edit_account.labels.optional')}
        </InputCompound.Label>
        <InputCompound.Box>
          <InputCompound.Section>
            <InputCompound.Area
              onChangeText={(text) => onEditAccount('routeStop', text)}
              placeholder={i18n.t(
                'sales_portal.edit_account.placeholders.route_stop',
              )}
              value={values.routeStop}
            />
          </InputCompound.Section>
        </InputCompound.Box>
        {errors.routeStop ? (
          <InputCompound.Info>{errors.routeStop}</InputCompound.Info>
        ) : null}
      </InputCompound>

      <InputCompound error={!!errors.note} size="$44">
        <InputCompound.Label>
          {i18n.t('sales_portal.edit_account.labels.note')}
          {i18n.t('sales_portal.edit_account.labels.optional')}
        </InputCompound.Label>
        <InputCompound.Box>
          <InputCompound.Section>
            <InputCompound.Area
              multiline
              numberOfLines={5}
              onChangeText={(text) => onEditAccount('note', text)}
              placeholder={i18n.t(
                'sales_portal.edit_account.placeholders.note',
              )}
              value={values.note}
            />
          </InputCompound.Section>
        </InputCompound.Box>
        {errors.note ? (
          <InputCompound.Info>{errors.note}</InputCompound.Info>
        ) : null}
      </InputCompound>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  form: {
    gap: theme.sk.spacing.spaceSMedium,
  },
}));
